import { useCallback, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

/**
 * **What the reader is looking at, held entirely in the URL.**
 *
 * `APPLICATION_SPEC.md` §2.2 asks for the channel filter and date range to
 * apply to every dashboard view, with the state in the query string so a view
 * can be linked and shared and survives a reload.
 *
 * That was half true until 8 Sep: the range and the channel list were in the
 * URL, while the **dataset tab, the language group and the lifetime window were
 * component state** — so a shared `/stories` link did not reproduce what the
 * sender was looking at. All four dimensions now live here.
 *
 * The four, and why each is one control rather than two:
 *
 * | In the URL | Answers | Note |
 * |---|---|---|
 * | `view` | which dataset | page-specific values; the page validates |
 * | `group` **or** `channels` | which channels | a group *is* a channel scope, so they are one control and mutually exclusive |
 * | `period` | which window | includes `lifetime`, because Snap's windowless total is a period like any other |
 * | `from`/`to` | which window, exactly | present **only** for `period=custom`; derived otherwise, so a named window follows today rather than freezing |
 */

export type LanguageGroup = 'english' | 'international'

/** `all` is explicit rather than implied by absence, because absence means
 *  *"use this page's default"* — and the default is English on the two asset
 *  pages and the whole estate on Channel Overview, which has an
 *  International-Combined chart to draw. */
export type Scope = LanguageGroup | 'all'

/** The contracted history start. The pipeline captures earlier days where the
 *  rolling window still allows, but this is the floor the dashboards offer. */
export const HISTORY_START = '2026-07-20'

/**
 * The client spec's fixed windows, as named periods.
 *
 * The spec asks for *Public Stories (Last 30 Days)*, *(Lifetime)* and
 * *Spotlights (This Year)* as separate tables — Q-I10. With one period control
 * those are the same table at four settings, so they ship as named options
 * instead: nothing contracted is lost, and we avoid shipping tables that differ
 * only by a filter the reader cannot see.
 *
 * `history` is deliberately not called "Lifetime" — Q-C4 is unanswered and our
 * collected history starts on 20 July 2026, so the label says what it covers.
 * `lifetime` is a different thing entirely: Snap's own total per asset, with no
 * window at all.
 */
export const PERIODS = ['last7', 'last14', 'last30', 'ytd', 'history', 'custom', 'lifetime'] as const

export type Period = (typeof PERIODS)[number]

export const PERIOD_LABELS: Record<Period, string> = {
  // 7 Oct (Nilay): weekly and fortnightly check-ins. Both compare cleanly —
  // their prior windows always sit inside the collected history.
  last7: 'Last 7 Days',
  last14: 'Last 14 Days',
  last30: 'Last 30 Days',
  ytd: 'This Year',
  history: 'Since 20 Jul 2026',
  custom: 'Custom Range',
  lifetime: 'Snap Lifetime',
}

export const PERIOD_NOTES: Record<Period, string> = {
  last7: 'The 7 Days Ending Yesterday. Today Is Still Counting.',
  last14: 'The 14 Days Ending Yesterday. Today Is Still Counting.',
  last30: 'The 30 Days Ending Yesterday. Today Is Still Counting.',
  ytd: '1 January to yesterday. We have no data before 20 July 2026.',
  history: 'Everything We Have, from 20 July 2026 to Yesterday.',
  custom: 'Any Two Dates. Both Ends Inclusive.',
  lifetime: 'Snap’s Own Totals, with No Date Range.',
}

function yesterday(): string {
  // Today is still accumulating, and Snap's day is UTC, so including it would
  // show every channel mid-day and read as a collapse in views.
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - 1)
  return date.toISOString().slice(0, 10)
}

export function periodRange(period: Period): { from: string; to: string } {
  const to = yesterday()
  if (period === 'history' || period === 'lifetime') return { from: HISTORY_START, to }
  if (period === 'ytd') {
    const year = new Date(`${to}T00:00:00Z`).getUTCFullYear()
    return { from: `${year}-01-01`, to }
  }
  // Inclusive of both ends, so 30 days is `to` minus 29. Off by one here would
  // quietly make every "Last 30 days" figure a 31-day figure.
  const days = period === 'last7' ? 7 : period === 'last14' ? 14 : 30
  const start = new Date(`${to}T00:00:00Z`)
  start.setUTCDate(start.getUTCDate() - (days - 1))
  return { from: start.toISOString().slice(0, 10), to }
}

/**
 * The window immediately before this one, of equal length — what a delta is
 * measured against.
 *
 * Equal length matters: comparing a 49-day window against a 30-day one produces
 * a delta that is mostly an artefact of the window, and it looks exactly like a
 * real decline.
 */
export function precedingRange(from: string, to: string): { from: string; to: string } {
  const start = new Date(`${from}T00:00:00Z`)
  const end = new Date(`${to}T00:00:00Z`)
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
  const prevEnd = new Date(start)
  prevEnd.setUTCDate(prevEnd.getUTCDate() - 1)
  const prevStart = new Date(prevEnd)
  prevStart.setUTCDate(prevStart.getUTCDate() - (days - 1))
  return { from: prevStart.toISOString().slice(0, 10), to: prevEnd.toISOString().slice(0, 10) }
}

export interface Filters {
  /** Explicit channel handles. Empty when a group or the whole estate is selected. */
  channels: string[]
  /** The language-group scope, or `all`. **Null means the page's default** — it
   *  is not the same as `all`. */
  group: Scope | null
  period: Period
  /** Resolved window. On `lifetime` these are the collected-history bounds, which
   *  the asset endpoints ignore — they are still passed so the daily-row views on
   *  the same page keep working. */
  from: string
  to: string
  /** Which dataset the page is showing. Values are the page's own. */
  view: string | null
}

export interface FilterActions {
  setChannels: (next: string[]) => void
  setGroup: (next: Scope) => void
  setPeriod: (next: Period) => void
  /** Switches to `custom` — an explicit range is by definition not a named one. */
  setRange: (from: string, to: string) => void
  /** `period` may be reset in the same write: switching to a view that cannot
   *  answer Snap lifetime has to move both, and two separate writes would lose
   *  the first (both read the same `params` closure). */
  setView: (next: string, period?: Period) => void
}

/**
 * **The window a view opens on, when the URL does not say.**
 *
 * The client's spec asks for different windows on different tables, and until
 * 10 Sep every view opened on `last30` regardless — so Saved Stories and
 * Spotlights, which the doc specifies at *Lifetime*, opened on a 30-day slice
 * and looked nearly empty.
 *
 * **Keyed on route + view, and resolved here rather than passed as a prop**,
 * because `useFilters` is called by the page *and* by both header controls. A
 * per-caller default would let them disagree — the picker showing one window
 * while the table fetched another, which is the class of bug the whole
 * URL-as-state design exists to prevent.
 *
 * An explicit `?period=` in the URL always wins, so a shared link still
 * reproduces exactly what the sender was looking at.
 */
const DEFAULT_PERIOD: { match: (path: string, view: string | null) => boolean; period: Period }[] = [
  // Saved Stories: spec tables 3 and 6 are both *Lifetime*, and it is the only
  // window where Viewers is a real deduplicated figure.
  { match: (path, view) => path.startsWith('/stories') && view === 'saved', period: 'lifetime' },
  // Channel Daily opens on our collected history, which is the client doc's
  // *(Lifetime)* read as "no date filter". Snap's own per-channel lifetime is
  // offered too (CO-8) but is not the default: it is a different table on a
  // window Snap chooses per metric, so it answers a different question.
  { match: (path, view) => path.startsWith('/stories') && view === 'daily', period: 'history' },
  // Spotlights: the spec's own table is Lifetime, and Snap carries 1-2.5 years
  // here that our daily rows cannot reproduce.
  { match: (path) => path.startsWith('/spotlights'), period: 'lifetime' },
]

function defaultPeriod(pathname: string, view: string | null): Period {
  return DEFAULT_PERIOD.find((rule) => rule.match(pathname, view))?.period ?? 'last30'
}

/**
 * Reads the period, and **treats bare dates as a custom range**.
 *
 * Links of the form `/channels?from=…&to=…` predate the period control and are
 * already in circulation. Ignoring their dates in favour of the default window
 * would silently show the wrong period to anyone opening one, which is the
 * quietest kind of wrong — the page looks fine.
 */
function parsePeriod(params: URLSearchParams, pathname: string): Period {
  const value = params.get('period')
  if (PERIODS.includes(value as Period)) return value as Period
  if (params.get('from') || params.get('to')) return 'custom'
  return defaultPeriod(pathname, params.get('view'))
}

export function useFilters(): Filters & FilterActions {
  const [params, setParams] = useSearchParams()
  // The route participates in the default window — see DEFAULT_PERIOD.
  const { pathname } = useLocation()

  const filters = useMemo<Filters>(() => {
    const period = parsePeriod(params, pathname)
    const group = params.get('group')
    // A named period is derived, so it tracks today rather than freezing on the
    // day the link was made. Only `custom` reads dates out of the URL.
    const range = period === 'custom' ? null : periodRange(period)
    return {
      channels: params.get('channels')?.split(',').filter(Boolean) ?? [],
      group: group === 'english' || group === 'international' || group === 'all' ? group : null,
      period,
      from: range?.from ?? params.get('from') ?? periodRange('last30').from,
      to: range?.to ?? params.get('to') ?? periodRange('last30').to,
      view: params.get('view'),
    }
  }, [params, pathname])

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params)
      for (const [key, value] of Object.entries(patch)) {
        if (value) next.set(key, value)
        else next.delete(key)
      }
      // replace: a filter change should not fill the back button with steps.
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  return {
    ...filters,
    // Scope is one answer, so picking channels clears the group and vice versa.
    // Two live scope controls is the duplication this replaced.
    setChannels: (next) =>
      update({ channels: next.length ? next.join(',') : null, group: null }),
    setGroup: (next) => update({ group: next, channels: null }),
    setPeriod: (next) =>
      update(
        next === 'custom'
          ? // Seed the custom inputs with the window already on screen, so
            // switching to Custom never moves the data underneath the reader.
            { period: 'custom', from: filters.from, to: filters.to }
          : { period: next, from: null, to: null },
      ),
    setRange: (from, to) => update({ period: 'custom', from, to }),
    setView: (next, period) =>
      update({ view: next, ...(period ? { period, from: null, to: null } : {}) }),
  }
}
